"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Bell, CheckCheck, ExternalLink, Info, AlertCircle } from "lucide-react";
import { fetchNotifications, markNotificationAsRead } from "@/app/api";

const DEFAULT_NOTIFS = [
  {
    id: 1,
    title: "خوش آمدید به پلتفرم پراپ تریدینگ PassFund",
    message: "سرورهای متاتریدر ۵ فعال و موتور ارزیابی قوانین به‌صورت خودکار هر ۱۵ دقیقه اجرا می‌شود.",
    is_read: 0,
    created_at: new Date().toISOString()
  },
  {
    id: 2,
    title: "قوانین دراداون روزانه و کل",
    message: "حداکثر افت سرمایه روزانه ۵٪ و افت سرمایه کل ۱۰٪ می‌باشد. معاملات در زمان اخبار مجاز است.",
    is_read: 1,
    created_at: new Date(Date.now() - 86400000).toISOString()
  }
];

export default function NotificationBellDropdown({ lang }: { lang: string }) {
  const isFa = lang === "fa";
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>(DEFAULT_NOTIFS);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadNotifs = async () => {
    try {
      const data = await fetchNotifications();
      if (Array.isArray(data) && data.length > 0) {
        setNotifications(data);
      } else {
        setNotifications(DEFAULT_NOTIFS);
      }
    } catch {
      // Keep default notifications on error or unauthenticated state
      setNotifications(DEFAULT_NOTIFS);
    }
  };

  useEffect(() => {
    loadNotifs();
    const timer = setInterval(loadNotifs, 20000);
    return () => clearInterval(timer);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await markNotificationAsRead(id);
    } catch {
      // Ignored
    }
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const unreadList = notifications.filter(n => !n.is_read);
    setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    for (const notif of unreadList) {
      try {
        await markNotificationAsRead(notif.id);
      } catch {
        // Ignored
      }
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div ref={dropdownRef} style={{ position: "relative", display: "inline-block" }}>
      {/* Bell Trigger Button */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        aria-label={isFa ? "اعلان‌های سیستم" : "System Notifications"}
        title={isFa ? "اعلان‌های سیستم" : "System Notifications"}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "38px",
          height: "38px",
          borderRadius: "10px",
          backgroundColor: isOpen ? "rgba(14, 165, 233, 0.15)" : "#f8fafc",
          border: `1px solid ${isOpen ? "#0ea5e9" : "#e2e8f0"}`,
          color: isOpen ? "#0ea5e9" : "#475569",
          cursor: "pointer",
          transition: "all 0.2s ease"
        }}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: "-4px",
              right: isFa ? "auto" : "-4px",
              left: isFa ? "-4px" : "auto",
              minWidth: "18px",
              height: "18px",
              padding: "0 4px",
              borderRadius: "10px",
              backgroundColor: "#ef4444",
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 6px rgba(239, 68, 68, 0.5)",
              border: "2px solid #ffffff",
              boxSizing: "border-box"
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div
          dir={isFa ? "rtl" : "ltr"}
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: isFa ? 0 : "auto",
            left: isFa ? "auto" : 0,
            width: "340px",
            maxWidth: "90vw",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 12px 30px rgba(0, 0, 0, 0.12)",
            zIndex: 1000,
            overflow: "hidden",
            animation: "fadeIn 0.15s ease-out"
          }}
        >
          {/* Dropdown Header */}
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "#f8fafc"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Bell size={16} color="#0ea5e9" />
              <span style={{ fontWeight: "bold", fontSize: "14px", color: "#1e293b" }}>
                {isFa ? "اعلان‌های سیستم" : "System Notifications"}
              </span>
              {unreadCount > 0 && (
                <span style={{ fontSize: "11px", backgroundColor: "rgba(14, 165, 233, 0.1)", color: "#0ea5e9", padding: "2px 6px", borderRadius: "10px", fontWeight: "bold" }}>
                  {unreadCount} {isFa ? "جدید" : "new"}
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: "none",
                  border: "none",
                  color: "#0ea5e9",
                  fontSize: "11px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}
              >
                <CheckCheck size={13} />
                {isFa ? "خواندن همه" : "Mark all read"}
              </button>
            )}
          </div>

          {/* Dropdown Notification Items List */}
          <div style={{ maxHeight: "320px", overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "30px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>
                {isFa ? "هیچ اعلانی در سیستم ثبت نشده است." : "No notifications available."}
              </div>
            ) : (
              notifications.map((notif) => {
                const isUnread = !notif.is_read;
                return (
                  <div
                    key={notif.id}
                    onClick={(e) => isUnread && handleMarkAsRead(notif.id, e)}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid #f1f5f9",
                      backgroundColor: isUnread ? "rgba(14, 165, 233, 0.04)" : "#ffffff",
                      cursor: isUnread ? "pointer" : "default",
                      transition: "background-color 0.15s ease"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", marginBottom: "4px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {isUnread && (
                          <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#0ea5e9", flexShrink: 0 }} />
                        )}
                        <span style={{ fontWeight: isUnread ? "bold" : "600", fontSize: "13px", color: isUnread ? "#0f172a" : "#475569" }}>
                          {notif.title}
                        </span>
                      </div>
                      <span style={{ fontSize: "10px", color: "#94a3b8", whiteSpace: "nowrap" }}>
                        {new Date(notif.created_at + (String(notif.created_at).endsWith("Z") ? "" : "Z")).toLocaleTimeString(isFa ? "fa-IR" : "en-US", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>

                    <p style={{ margin: 0, fontSize: "12px", color: "#64748b", lineHeight: "1.5" }}>
                      {notif.message}
                    </p>
                  </div>
                );
              })
            )}
          </div>

          {/* Dropdown Footer */}
          <div
            style={{
              padding: "10px 16px",
              backgroundColor: "#f8fafc",
              borderTop: "1px solid #f1f5f9",
              textAlign: "center"
            }}
          >
            <Link
              href={`/${lang}/panel/notifications`}
              onClick={() => setIsOpen(false)}
              style={{
                fontSize: "12px",
                color: "#0ea5e9",
                fontWeight: "bold",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "5px"
              }}
            >
              <span>{isFa ? "مشاهده تمام اعلان‌ها در صفحه اختصاصی" : "View all notifications"}</span>
              <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
